import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Direction_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { Status_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { FaxQueries, FaxTypes } from '@frontend/api-fax';
import { FaxMediaQueries } from '@frontend/api-fax-media';
import { TagsV2 } from '@frontend/api-tag';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { SchemaFaxService } from '@frontend/schema';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { TagSelectionComponents, TagSelectionHooks } from '@frontend/tag-selection';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Avatar, Heading, Text, phone, useAlert, Button, Chip, useTooltip } from '@frontend/design-system';
import { useFaxActions } from '../../../hooks/use-fax-actions';
import { useSelectedFaxShallowStore } from '../../../stores/selected-fax-store';
import { ThreadHeaderActions } from './thread-header-actions';
import { ThreadHeaderTags } from './thread-header-tags';
import { ThreadViewBody } from './thread-view-body';
import { ThreadViewStateIndicator } from './thread-view-status-indicator';

type Props = {
  faxId: string;
};

export const ThreadView = ({ faxId }: Props) => {
  const { t } = useTranslation('fax');
  const search = useSearch({ from: '/fax/$filter/$faxId', shouldThrow: false });
  const isNotification = search?.isNotification;
  const alert = useAlert();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const navigate = useNavigate();
  const { selectedFax, setSelectedFax, resetSelectedFax } = useSelectedFaxShallowStore(
    'selectedFax',
    'setSelectedFax',
    'resetSelectedFax'
  );
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const locationId = selectedFax?.locationId || selectedLocationIds[0];
  const [profileMedia, setProfileMedia] = useState('');
  const [isTagButtonHovered, setIsTagButtonHovered] = useState(false);
  const { setShow } = useSlidePanelShallowStore('setShow');
  const { navigate: settingsNavigate } = useSettingsNavigate();

  const { tagFax } = useFaxActions();

  // For opening the thread view from a notification, we need to fetch the fax details.
  const {
    data: currentFax,
    isLoading: isLoadingFax,
    isFetching: isFetchingFax,
    error: isGetFaxError,
  } = FaxQueries.useGetFax(faxId, locationId, {
    enabled: !!faxId && !!isNotification,
    cacheTime: 0,
    staleTime: 0,
    retry: 1,
  });

  const {
    mutateAsync: generateMediaAttributes,
    isLoading: isLoadingMediaAttributes,
    isIdle: isGenerateMediaAttributesIdle,
  } = FaxMediaQueries.useGenerateMediaAttributes({
    onError: () => {
      alert.error(t('Failed to retrieve fax info. Please try again.'));
    },
  });

  useEffect(() => {
    return () => {
      resetSelectedFax();
    };
  }, []);

  // Mark fax as read on opening it from notification.
  useEffect(() => {
    if (!currentFax?.id || !isNotification || isFetchingFax) {
      return;
    }
    if (currentFax.direction === Direction_Enum.INBOUND && !currentFax.viewedAt) {
      SchemaFaxService.UpdateFaxes({
        hasBeenRead: true,
        locationId,
        faxIds: [currentFax.id],
        locationPhones: currentFax.locationPhone ? [currentFax.locationPhone] : [],
      });
    }
  }, [currentFax, isNotification, isFetchingFax]);

  // This useEffect and ref handle the issue of not loading the fax thumbnail images / data
  // for the selectedFax from notification.
  const seededFaxIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isNotification || !currentFax?.id || isFetchingFax) {
      return;
    }
    if (currentFax.id !== faxId) {
      return;
    }
    // Already seeded for this fax (by us or by an InboxListItem), do nothing.
    if (seededFaxIdRef.current === faxId || selectedFax.faxId === faxId) {
      return;
    }
    seededFaxIdRef.current = faxId;
    const personName = `${currentFax.faxContact?.name ?? ''} ${currentFax.faxContact?.secondName ?? ''}`.trim();
    setSelectedFax({
      faxId: currentFax.id,
      mediaId: currentFax.mediaId ?? '',
      contactName: personName,
      contactPhone: currentFax.contactPhone ?? '',
      contactId: currentFax.faxContact?.contactId ?? '',
      profileMediaId: currentFax.faxContact?.profileMediaId ?? '',
      profileMedia: '',
      locationId: currentFax.locationId ?? locationId,
      locationPhone: currentFax.locationPhone ?? '',
      direction: currentFax.direction,
      provider: currentFax.provider,
      status: currentFax.status ?? '',
      isArchived: !!currentFax.archivedAt,
      isBlocked: !!currentFax.faxContact?.isBlocked,
      tags: currentFax.tags ?? [],
      thumbnailIds: [],
      pages: 0,
    });
  }, [currentFax?.id, isNotification, isFetchingFax, faxId, selectedFax.faxId]);

  useEffect(() => {
    if (!selectedFax.thumbnailIds.length && selectedFax.mediaId && locationId) {
      generateMediaAttributes({
        mediaId: selectedFax.mediaId ?? '',
        locationId,
        direction: selectedFax.direction,
        provider: selectedFax.provider,
      }).then((res) => {
        setSelectedFax({
          ...selectedFax,
          pages: res.attributes.pages,
          thumbnailIds: res.attributes.largeThumbnails ?? [],
        });
      });
    }
  }, [selectedFax.faxId, selectedFax.mediaId, locationId, selectedFax.thumbnailIds.length]);

  const { data: blob } = FaxQueries.useGetFaxContactMedia(selectedFax?.profileMediaId ?? '', selectedFax.locationId, {
    enabled: !!selectedFax?.profileMediaId,
  });

  const formattedPhone =
    currentFax?.contactPhone === 'Unknown' || selectedFax.contactPhone === 'Unknown'
      ? t('Unknown')
      : phone((currentFax?.contactPhone || selectedFax.contactPhone) ?? '', { hideCountry: true });

  const locationTagsQuery = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: [locationId] },
  });

  const locationTags = locationTagsQuery.data?.tags ?? [];

  const isOutbound = selectedFax.direction === Direction_Enum.OUTBOUND;
  const senderLocationPhone = isOutbound ? selectedFax.locationPhone || currentFax?.locationPhone || '' : '';
  const senderDisplayName = isOutbound ? selectedFax.locationPhoneDisplayName || '' : '';
  const formattedSenderPhone = senderLocationPhone ? phone(senderLocationPhone) : '';

  const {
    Tooltip: SentFromTooltip,
    triggerProps: sentFromTooltipTriggerProps,
    tooltipProps: sentFromTooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'bottom',
    offset: 8,
  });

  const tags = locationTags.filter((tag) => selectedFax.tags?.includes(tag.id || tag.smartTagId));

  const mobileContactName =
    selectedFax.contactName === 'Unknown' || !selectedFax.contactName ? formattedPhone : selectedFax.contactName;

  const [tagSearchValue, setTagSearchValue] = useState<string>('');

  const tagIdsToExclude = tags.map((tag) => tag.id) ?? [];

  const addTag = async (tag: Tag) => {
    const tagId = tag.id || tag.smartTagId;
    try {
      await tagFax(locationId, [faxId], [tagId]);
      if (selectedFax.faxId === faxId) {
        setSelectedFax({
          ...selectedFax,
          tags: [...selectedFax.tags, tagId],
        });
      }
    } catch (error) {
      console.error('Error tagging fax', error);
    }
  };

  const {
    popoverProps: tagPopoverProps,
    open: openTagSelection,
    isOpen: tagSelectionIsOpen,
    close: closeTagSelection,
    getTriggerProps: getTagSelectionTriggerProps,
  } = TagSelectionHooks.useTagSelectionPopover({
    onClose: () => {
      setTagSearchValue('');
      closeTagSelection();
    },
    onTagSelect: async (tag) => {
      addTag(tag);
      closeTagSelection();
    },
    onTagCreate: (initName) => {
      settingsNavigate({
        to: '/tags/quick-create',
        context: {
          onCreate: (tag: Tag) => {
            addTag(tag);
          },
          name: initName,
        },
      });
    },
    trackingIds: {
      selectTag: `${FaxPrefixes.Tag}-add-existing-tag`,
      createTag: `${FaxPrefixes.Tag}-create-new-tag`,
      createNamedTag: `${FaxPrefixes.Tag}-create-new-tag-with-name`,
      openTagSettings: `${FaxPrefixes.Tag}-open-tag-settings`,
    },
    searchValue: tagSearchValue,
    tagIdsToExclude,
    placement: 'bottom-start',
    initialOffset: { x: -2, y: 0 },
  });

  const openContactForm = () => {
    if (selectedFax.contactId) {
      setShow(true, 'faxContactForm', {
        contactId: selectedFax.contactId,
        locationId: selectedFax.locationId,
        action: FaxTypes.ContactAction.EDIT,
      });
    } else {
      setShow(true, 'faxContactForm', {
        locationId: selectedFax.locationId,
        action: FaxTypes.ContactAction.ADD,
      });
    }
  };

  useEffect(() => {
    if (blob) {
      const url = URL.createObjectURL(blob);
      setProfileMedia(url);
    } else {
      setProfileMedia('');
    }

    return () => {
      if (profileMedia) {
        URL.revokeObjectURL(profileMedia);
      }
    };
  }, [blob]);

  // For handling error in opening the thread view from a notification flow
  if (isGetFaxError) {
    alert.error(t('Failed to retrieve fax info. Please try again.'));
    return <ThreadViewStateIndicator state='empty' />;
  }

  const isThreadLoading =
    isLoadingFax || isLoadingMediaAttributes || (selectedFax.mediaId && locationId && isGenerateMediaAttributesIdle);

  return (
    <>
      <div
        css={{
          display: 'flex',
          gap: theme.spacing(2),
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: theme.spacing(1.95, 2),
          borderBottom: `1px solid ${theme.colors.neutral20}`,
        }}
      >
        <div
          css={{
            display: 'flex',
            gap: theme.spacing(2),
            alignItems: 'center',
          }}
        >
          {isMobile && (
            <Button
              iconName='back'
              label='Back'
              onClick={() => {
                navigate({
                  to: '/fax/$filter',
                  params: { filter: 'inbox' },
                });
              }}
              size='small'
              variant='secondary'
            />
          )}
          <>
            {blob && profileMedia ? (
              <img
                css={{
                  height: theme.spacing(5),
                  width: theme.spacing(5),
                  minWidth: theme.spacing(5),
                  borderRadius: theme.borderRadius.full,
                }}
                src={profileMedia}
              />
            ) : (
              <Avatar
                name={selectedFax.contactName}
                onClick={(e) => {
                  e?.stopPropagation();
                  openContactForm();
                }}
              />
            )}
          </>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              css={{
                display: 'flex',
                columnGap: theme.spacing(1),
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Heading
                css={{
                  textAlign: isMobile ? 'center' : 'left',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
                level={2}
                data-trackingid={`${FaxPrefixes.Thread}-contact-header-title`}
              >
                {isMobile ? mobileContactName : selectedFax.contactName}
              </Heading>

              {!isMobile && (
                <Text
                  size='medium'
                  css={{ color: theme.colorsV2.black(60) }}
                  data-trackingid={`${FaxPrefixes.Thread}-contact-header-to-picker`}
                >
                  {formattedPhone}
                </Text>
              )}
            </span>
          </div>
        </div>
        <Button
          iconName='user'
          hoverLabel={t('Edit Contact')}
          css={{ border: `1px solid ${theme.colorsV2.black(20)}`, flexShrink: 0 }}
          variant='secondary'
          onClick={() => openContactForm()}
        />
      </div>
      <div
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: theme.spacing(1, 2),
          boxShadow: `0px 2px 8px ${theme.colorsV2.black(10)}`,
          zIndex: 0,
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            columnGap: theme.spacing(1),
            maxWidth: '60%',
          }}
        >
          {selectedFax.direction === Direction_Enum.OUTBOUND && (
            <>
              <span
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  backgroundColor: theme.colorsV2.green50(10),
                  padding: theme.spacing(0.5),
                  columnGap: theme.spacing(0.5),
                  flexShrink: 0,
                }}
                {...sentFromTooltipTriggerProps}
              >
                <Icon name='trend-up-small' color='success' />
                {!isMobile && (
                  <Text color='success' size='small'>
                    {t('Sent')}
                  </Text>
                )}
              </span>
              {!!formattedSenderPhone && (
                <SentFromTooltip {...sentFromTooltipProps}>
                  {senderDisplayName ? (
                    <>
                      {t('Sent from {{name}}', { name: senderDisplayName })}
                      <br />
                      {formattedSenderPhone}
                    </>
                  ) : (
                    t('Sent from {{number}}', { number: formattedSenderPhone })
                  )}
                </SentFromTooltip>
              )}
            </>
          )}
          <div
            css={[
              {
                ...(selectedFax.direction === Direction_Enum.OUTBOUND && {
                  borderLeft: `1px solid ${theme.colors.neutral20}`,
                  paddingLeft: theme.spacing(1),
                }),
              },
              {
                display: 'flex',
                alignItems: 'center',
                columnGap: theme.spacing(0.5),
              },
            ]}
          >
            {(tags.length > 0 || tagSelectionIsOpen) && (
              <div>
                <ThreadHeaderTags
                  tags={tags}
                  locationId={locationId}
                  faxId={faxId}
                  tagSelectionIsOpen={tagSelectionIsOpen}
                />
                {tagSelectionIsOpen && (
                  <span data-taginput ref={getTagSelectionTriggerProps().ref}>
                    <Chip.Tag
                      editable
                      color='gray'
                      onKeyDown={(e: KeyboardEvent) => {
                        if (e.key === 'Escape') {
                          closeTagSelection();
                        }
                        if (e.key === 'Enter' && tagSelectionIsOpen) {
                          e.preventDefault();
                          if (tagPopoverProps.tagsList[0]) {
                            closeTagSelection();
                            addTag(tagPopoverProps.tagsList[0]);
                          } else {
                            settingsNavigate({
                              to: '/tags/quick-create',
                              context: { name: tagSearchValue },
                            });
                          }
                        }
                      }}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setTagSearchValue(e.currentTarget.value);
                      }}
                    />
                    <TagSelectionComponents.TagSelectionPopover
                      {...tagPopoverProps}
                      returnFocus={false}
                      initialFocus={-1}
                    />
                  </span>
                )}
              </div>
            )}
            <Button
              iconName={isTagButtonHovered && !tagSelectionIsOpen ? 'plus' : 'label'}
              size='small'
              variant='secondary'
              hoverLabel={t('Add Tag')}
              onMouseEnter={() => setIsTagButtonHovered(true)}
              onMouseLeave={() => setIsTagButtonHovered(false)}
              onClick={() => {
                tagSelectionIsOpen ? closeTagSelection() : openTagSelection();
              }}
            />
          </div>
        </div>
        <ThreadHeaderActions
          faxId={faxId}
          locationId={selectedFax.locationId}
          isFaxRetryable={selectedFax.status === Status_Enum.FAILED_RETRYABLE}
        />
      </div>
      {isThreadLoading ? (
        <ThreadViewStateIndicator state='loading' />
      ) : !!selectedFax.thumbnailIds?.length ? (
        <ThreadViewBody key={faxId} />
      ) : (
        <ThreadViewStateIndicator state='empty' />
      )}
    </>
  );
};
